"use client";

import React, { Suspense, useCallback, useRef } from "react";
import { QueryClientProvider, QueryClient } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { ThemeProvider } from "@mui/material";
import theme from "./theme";
import { FeedBackProvider } from "../providers/FeedBackProvider/FeedBackProvider";
import PDFGeneratorProvider from "../providers/PDFGeneratorProvider/PDFGeneratorProvider";
import XlsGeneratorProvider from "../providers/XlsGeneratorProvider/XlsGeneratorProvider";
import { RegisterBeneficiaryModalProvider } from "../components/modals/RegisterBeneficiary/RegisterBeneficiaryModalProvider";
import Spinner from "../components/Spinner";
import { axios } from "../utils/axios.create";

function Providers({ children }: React.PropsWithChildren) {
	const [client] = React.useState(new QueryClient());
	// eslint-disable-next-line @typescript-eslint/no-var-requires
	const { QlikEmbedConfig } = require("@qlik/embed-react");

	const tokenRef = useRef(null);

	const getAccessToken = useCallback(async () => {
		if (tokenRef.current) {
			return tokenRef.current;
		}
		try {
			const response = await axios.post(
				"/qlik-token?email=dummy_email@email.com",
				{},
			);
			tokenRef.current = response.data.body.qlikToken;
			return tokenRef.current;
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error("Error fetching access token", error);
			throw error;
		}
	}, []); // no dependency on token

	const hostConfig = {
		host: "https://cmi.us.qlikcloud.com",
		authType: "oauth2",
		clientId: process.env.REACT_APP_QLIK_CLIENT_ID || "",
		noCache: true,
		getAccessToken: getAccessToken,
	};

	return (
		<QlikEmbedConfig.Provider value={hostConfig}>
			<QueryClientProvider client={client}>
				<ThemeProvider theme={theme}>
					<FeedBackProvider>
						<PDFGeneratorProvider>
							<XlsGeneratorProvider>
								<RegisterBeneficiaryModalProvider>
									<Suspense fallback={<Spinner />}>{children}</Suspense>
								</RegisterBeneficiaryModalProvider>
							</XlsGeneratorProvider>
						</PDFGeneratorProvider>
					</FeedBackProvider>
				</ThemeProvider>
				<ReactQueryDevtools initialIsOpen={false} />
			</QueryClientProvider>
		</QlikEmbedConfig.Provider>
	);
}

export default Providers;
